// La table de marque sur le téléphone (D-176, « Hors réseau ») : le journal, la file d'envoi et l'état restent sur
// l'appareil, même après rechargement ; ils partent dans l'ordre au retour du réseau, sans doublon (identifiants tirés
// sur l'appareil). L'identifiant de l'appareil est tiré au hasard une fois ; le prénom facultatif est oublié le soir.
import type { Action } from '../domain/boxscore';
import { readState, type Op, type TableState } from '../domain/table';
import { TableError } from './table-api';

const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const put = (k: string, v: string | null) => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* stockage plein ou privé */ } };
const today = () => new Date().toLocaleDateString('fr-CA');

/** L'identifiant de ce téléphone pour les tables (32 caractères hexadécimaux, tiré une fois). */
export function deviceKey(): string {
  const k = get('arceau:table-device');
  if (k && /^[0-9a-f]{32}$/.test(k)) return k;
  const b = new Uint8Array(16);
  try { crypto.getRandomValues(b); } catch { for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256); }
  const v = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  put('arceau:table-device', v);
  return v;
}
/** Le prénom tapé à l'ouverture : gardé la journée seulement. */
export function savedName(): string {
  try { const x = JSON.parse(get('arceau:table-name') ?? 'null') as { n: string; d: string } | null; return x && x.d === today() ? x.n : ''; } catch { return ''; }
}
export const saveName = (n: string) => put('arceau:table-name', n.trim() ? JSON.stringify({ n: n.trim().split(/\s+/)[0]!.slice(0, 24), d: today() }) : null);

/** Ce que le téléphone garde d'une table ; `ctx` : de quoi la rouvrir sans réseau (équipes, joueuses du jour, lien). */
export interface LocalTable { v: 1; actions: Action[]; queue: Op[]; state: TableState; savedAt: number; ctx?: Record<string, unknown>; offset?: number }
const keyOf = (id: string) => `arceau:table:${id}`;
export function loadLocal(id: string): LocalTable | null {
  try {
    const x = JSON.parse(get(keyOf(id)) ?? 'null') as LocalTable | null;
    if (!x || x.v !== 1 || !Array.isArray(x.actions) || !Array.isArray(x.queue)) return null;
    const state = readState(x.state, Date.now());
    return state ? { ...x, state } : null;
  } catch { return null; }
}
export const saveLocal = (id: string, t: Omit<LocalTable, 'v' | 'savedAt'>) => put(keyOf(id), JSON.stringify({ v: 1, ...t, savedAt: Date.now() }));
export const dropLocal = (id: string) => put(keyOf(id), null);

/** Le lien de saisie (D-057) d'une table : retrouver son match sans réseau. */
export const linkEvent = (token: string) => get(`arceau:table-link:${token}`);
export const setLinkEvent = (token: string, event: string) => put(`arceau:table-link:${token}`, event);

/** Le QR « Passer la main » scanné : l'adresse /t/<jeton> retrouve son match au rechargement. */
export const takenEvent = (token: string) => get(`arceau:table-taken:${token}`);
export const setTakenEvent = (token: string, event: string) => put(`arceau:table-taken:${token}`, event);

/**
 * L'envoi : la file part dans l'ordre (un envoi à la fois), avec l'état de la table ; réseau absent : on réessaie
 * toutes les 5 s et au retour du réseau. « denied » : un autre téléphone tient la table ; « closed » : match terminé.
 */
export class TableSender {
  private busy = false;
  private timer = 0;
  private dirty = false;
  offline = false;
  /** Les actions en route vers la base : les annuler envoie un effacement (elles ne sont plus seulement sur le téléphone). */
  readonly inflight = new Set<string>();
  /** `send` : l'envoi à la base (table_sync d'un match du club, tmatch_sync d'un match de tournoi). */
  constructor(private readonly send: (ops: Op[], state: TableState) => Promise<{ skipped?: string[] }>,
    private readonly take: () => { queue: Op[]; state: TableState },
    private readonly done: (sent: Op[], skipped: string[]) => void,
    private readonly fail: (kind: TableError['kind']) => void) {
    window.addEventListener('online', this.onOnline);
  }
  private onOnline = () => { void this.flush(); };
  stop(): void { window.removeEventListener('online', this.onOnline); clearTimeout(this.timer); }
  /** Quelque chose a changé (une action, le chrono, la période) : on envoie bientôt. */
  kick(delay = 250): void { this.dirty = true; clearTimeout(this.timer); this.timer = window.setTimeout(() => void this.flush(), delay); }
  get pending(): boolean { return this.busy || this.dirty; }
  async flush(): Promise<boolean> {
    if (this.busy) { this.dirty = true; return false; }
    this.busy = true; this.dirty = false;
    const { queue, state } = this.take();
    const ops = queue.slice(0, 200);
    for (const o of ops) if (o.op === 'add') this.inflight.add(o.a.id);
    try {
      const r = await this.send(ops, state);
      this.offline = false;
      this.inflight.clear();
      this.done(ops, r.skipped ?? []);
      this.busy = false;
      if (this.dirty || queue.length > ops.length) this.kick(50);
      return true;
    } catch (e) {
      this.busy = false;
      this.inflight.clear();
      const kind = e instanceof TableError ? e.kind : 'network';
      if (kind === 'network') { this.offline = true; this.dirty = true; clearTimeout(this.timer); this.timer = window.setTimeout(() => void this.flush(), 5000); }
      this.fail(kind);
      return false;
    }
  }
}
